import React from 'react';

// services
import {CreditBookApi} from '../../shared/services/ApiService';

// helpers
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

// components
import {CardEmptyState, CardError, PageTemplate} from '../../shared/components';
import {CardArticle} from './components';

// styles
import {CardArticlesWrapper, IconNoArticles} from './ArticlesPage-styles';

// types
import {
  ArticlePreview,
  CreditBookApiError,
} from '../../shared/services/ApiService/types';
import {LoadingPage} from '../LoadingPage';

/**
 * Fetch the articles
 */
const fetchArticles = async (articleType: string | undefined) => {
  const {data} = await CreditBookApi.article.getArticles(articleType || "");
  return data;
};

type ArticlePageTitle =
  | 'article_contentLibrary'
  | 'article_contentLibrary_blog'
  | 'article_contentLibrary_video';

const ArticlesPage = () => {
  const {t} = useTranslation();
  const {articleType} = useParams();

  const {
    data: articles = [],
    isLoading: articlesLoading,
    error: articlesError,
    isRefetching: articlesRefetching,
  } = useQuery<ArticlePreview[], CreditBookApiError>({
    queryKey: ['getArticles', articleType],
    queryFn: () => fetchArticles(articleType),
    initialData: [],
  });

  /**
   * Render the articles based on the states
   */
  const renderArticles = () => {
    if (articlesLoading || articlesRefetching) {
      return <LoadingPage usePortalConfig />;
    }

    if (articlesError) {
      return (
        <CardError message={articlesError?.response?.data?.errorMessage} />
      );
    }

    if (articles.length === 0) {
      return (
        <CardEmptyState text={t('article_emptyState')} icon={IconNoArticles} />
      );
    }

    return (
      <CardArticlesWrapper>
        {articles.map((article) => (
          <CardArticle key={article.id} article={article} />
        ))}
      </CardArticlesWrapper>
    );
  };

  const getPageTitle = (): ArticlePageTitle => {
    switch(articleType) {
      case 'blog':
        return 'article_contentLibrary_blog';
      case 'video':
        return 'article_contentLibrary_video';
      default:
        return 'article_contentLibrary';
    }
  };

  return (
    <PageTemplate title={t(getPageTitle())}>
      {renderArticles()}
    </PageTemplate>
  );
};

export default ArticlesPage;
